import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  GraduationCap,
  HeartPulse,
  Utensils,
  Scissors,
  Dna,
  Heart,
  Trophy,
  Plane,
  Camera,
  Shirt,
  Gamepad2,
  Clock,
  Baby,
  Brain,
  Shield,
  Loader2,
  Save,
  Sparkles,
  BellRing,
  BellOff,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'graduation-cap': GraduationCap,
  'heart-pulse': HeartPulse,
  'utensils': Utensils,
  'scissors': Scissors,
  'dna': Dna,
  'hand-heart': Heart,
  'trophy': Trophy,
  'plane': Plane,
  'camera': Camera,
  'shirt': Shirt,
  'gamepad': Gamepad2,
  'clock': Clock,
  'baby': Baby,
  'brain': Brain,
  'shield': Shield
};

const Interests = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({ interests: [], ad_categories: [] });
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [adPreference, setAdPreference] = useState('personalized');
  const [selectedAdCategories, setSelectedAdCategories] = useState([]);

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    if (user) {
      setSelectedInterests(user.interests || []);
      setAdPreference(user.ad_preference || 'personalized');
      setSelectedAdCategories(user.ad_categories || []);
    }
  }, [user]);

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API}/users/interests/options`);
      setOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch options');
    } finally {
      setLoading(false);
    }
  };

  const toggleInterest = (interestId) => {
    setSelectedInterests(prev => 
      prev.includes(interestId) 
        ? prev.filter(i => i !== interestId)
        : [...prev, interestId]
    );
  };

  const toggleAdCategory = (categoryId) => {
    setSelectedAdCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save interests
      await axios.put(`${API}/users/interests`, selectedInterests);
      
      // Save ad preferences
      await axios.put(`${API}/users/ad-preferences`, null, {
        params: {
          ad_preference: adPreference,
          ad_categories: selectedAdCategories
        },
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          searchParams.append('ad_preference', params.ad_preference);
          params.ad_categories.forEach(cat => searchParams.append('ad_categories', cat));
          return searchParams.toString();
        }
      });
      
      // Update local user
      updateUser({
        ...user,
        interests: selectedInterests,
        ad_preference: adPreference,
        ad_categories: selectedAdCategories
      });
      
      toast.success('Preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-medium mb-2 flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-primary" />
              Interests & Preferences
            </h1>
            <p className="text-muted-foreground">
              Customize your experience by selecting topics you're interested in
            </p>
          </div>

          {/* Interests Section */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Heart className="w-5 h-5 text-secondary" />
                Your Interests
              </CardTitle>
              <CardDescription>
                Select topics you'd like to see more content about
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {options.interests.map((interest) => {
                  const IconComponent = iconMap[interest.icon] || Heart;
                  const isSelected = selectedInterests.includes(interest.id);
                  return (
                    <button
                      key={interest.id}
                      onClick={() => toggleInterest(interest.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-border hover:border-primary/50 hover:bg-muted'
                      }`}
                    >
                      <IconComponent className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium text-left">{interest.label}</span>
                    </button>
                  );
                })}
              </div>
              
              {selectedInterests.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-2">Selected ({selectedInterests.length}):</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedInterests.map(id => {
                      const interest = options.interests.find(i => i.id === id);
                      return interest ? (
                        <Badge key={id} variant="secondary" className="cursor-pointer" onClick={() => toggleInterest(id)}>
                          {interest.label} ×
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ad Preferences Section */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <BellRing className="w-5 h-5 text-primary" />
                Ad Preferences
              </CardTitle>
              <CardDescription>
                Control what types of advertisements you see
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <RadioGroup value={adPreference} onValueChange={setAdPreference}>
                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50">
                  <RadioGroupItem value="personalized" id="personalized" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="personalized" className="font-medium cursor-pointer flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      Personalized Ads
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      See ads tailored to your interests and activity
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50">
                  <RadioGroupItem value="all" id="all" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="all" className="font-medium cursor-pointer flex items-center gap-2">
                      <Bell className="w-4 h-4 text-blue-500" />
                      All Ads
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      See all types of advertisements
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50">
                  <RadioGroupItem value="minimal" id="minimal" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="minimal" className="font-medium cursor-pointer flex items-center gap-2">
                      <Bell className="w-4 h-4 text-gray-500" />
                      Minimal Ads
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      See fewer ads, only essential promotions
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50">
                  <RadioGroupItem value="none" id="none" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="none" className="font-medium cursor-pointer flex items-center gap-2">
                      <BellOff className="w-4 h-4 text-red-500" />
                      No Ads
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Opt out of advertisements (Full Members only)
                    </p>
                  </div>
                </div>
              </RadioGroup>

              {/* Ad Categories */}
              {(adPreference === 'personalized' || adPreference === 'all') && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">Ad Categories</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Select categories of ads you're interested in seeing
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {options.ad_categories.map((category) => (
                      <div key={category.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`ad-${category.id}`}
                          checked={selectedAdCategories.includes(category.id)}
                          onCheckedChange={() => toggleAdCategory(category.id)}
                        />
                        <Label htmlFor={`ad-${category.id}`} className="text-sm cursor-pointer">
                          {category.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              className="btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Preferences
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Interests;
